import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Copy, Eye, FlaskConical, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import PaperCard, { PaperCardSkeleton } from "@/components/PaperCard";
import PaperSheet from "@/components/PaperSheet";
import EmptyState from "@/components/EmptyState";
import { useMeta } from "@/lib/useMeta";
import { fetchPapers } from "@/lib/papers";
import { useTaxonomies } from "@/lib/taxonomies";
import { activeTaxonomies } from "@/lib/taxonomy";
import { toneHue } from "@/lib/tone";
import { APP_URL } from "@/lib/config";
import { cn } from "@/lib/utils";

// হিরোর পাশের সাজানো শিট — নিছক সাজসজ্জা, নমুনা নাম (আসল কোনো প্রতিষ্ঠান না)
const HERO_SHEETS = [
  { seed: "hero-1", hue: 350, institution: "আদর্শ উচ্চ বিদ্যালয়", exam: "মডেল টেস্ট", cls: "নবম", subject: "বাংলা", pos: "-rotate-[7deg] -translate-x-[58%] translate-y-[6%]" },
  { seed: "hero-2", hue: 30, institution: "নূরানী দাখিল মাদ্রাসা", exam: "অর্ধ-বার্ষিক", cls: "সপ্তম", subject: "গণিত", pos: "rotate-[7deg] translate-x-[58%] translate-y-[10%]" },
  { seed: "hero-3", hue: 174, institution: "সরকারি বালিকা বিদ্যালয়", exam: "বার্ষিক পরীক্ষা", cls: "অষ্টম", subject: "বিজ্ঞান", pos: "z-10 -translate-y-[2%] scale-105" },
];

function HeroArt() {
  return (
    <div className="relative mx-auto hidden h-[24rem] w-full max-w-md items-center justify-center sm:flex" aria-hidden="true">
      <div className="absolute inset-x-6 inset-y-10 rounded-full bg-[radial-gradient(closest-side,hsl(var(--brand)/0.28),transparent)] blur-2xl" />
      {HERO_SHEETS.map((s) => (
        <div key={s.seed} style={{ "--tone-h": s.hue }} className={cn("tone absolute w-[46%]", s.pos)}>
          <PaperSheet seed={s.seed} institution={s.institution} exam={s.exam} classLabel={s.cls} subjectLabel={s.subject} />
        </div>
      ))}
    </div>
  );
}

function SectionHead({ title, children, action }) {
  return (
    <div className="mb-5 flex items-end justify-between gap-4">
      <div>
        <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">{title}</h2>
        {children && <p className="mt-1 text-sm text-muted-foreground">{children}</p>}
      </div>
      {action}
    </div>
  );
}

const STEPS = [
  { icon: Search, title: "খুঁজুন", text: "শ্রেণি, বিষয় আর পরীক্ষার ধরন দিয়ে ছেঁকে নিন, বা নাম লিখে খুঁজুন।" },
  { icon: Eye, title: "দেখে নিন", text: "পুরো প্রশ্নপত্র আসল চেহারায় দেখুন — ঠিক যেমন ছাপা হবে।" },
  { icon: Copy, title: "কপি করুন", text: "এক ট্যাপে নিজের একাউন্টে নিয়ে নিজের মতো বদলে নিন।" },
];

export default function Home() {
  useMeta({
    title: "মুমতাহিন গ্যালারি — শিক্ষকদের তৈরি প্রশ্নপত্র",
    description: "শিক্ষকদের নিজের তৈরি প্রশ্নপত্র খুঁজুন, কপি করুন, অথবা এখনই লাইভ ট্রাই করুন।",
  });

  const navigate = useNavigate();
  const taxonomies = useTaxonomies();
  const active = useMemo(() => activeTaxonomies(taxonomies), [taxonomies]);
  const classes = active.find((x) => x.key === "class");
  const subjects = active.find((x) => x.key === "subject");

  const [q, setQ] = useState("");
  const [data, setData] = useState({ recent: null, popular: null, failed: false });
  const [tab, setTab] = useState("recent");

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchPapers({ sort: "newest", limit: 6 }), fetchPapers({ sort: "most-copied", limit: 6 })])
      .then(([recent, popular]) => !cancelled && setData({ recent, popular: popular.filter((p) => (p.copy_count ?? 0) > 0), failed: false }))
      .catch(() => !cancelled && setData({ recent: null, popular: null, failed: true }));
    return () => {
      cancelled = true;
    };
  }, []);

  // "জনপ্রিয়" ট্যাব শুধু তখনই যখন সত্যিই কয়েকটা কপি হওয়া পেপার আছে
  const hasPopular = (data.popular?.length ?? 0) >= 3;
  const shown = tab === "popular" && hasPopular ? data.popular : data.recent;

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    navigate(term ? `/papers?q=${encodeURIComponent(term)}` : "/papers");
  };

  return (
    <>
      {/* হিরো */}
      <section className="relative isolate overflow-hidden border-b border-border/60">
        <div className="bg-ruled absolute inset-0 -z-10" aria-hidden="true" />
        <div className="container grid items-center gap-10 py-12 sm:py-16 lg:grid-cols-[1.15fr_0.85fr] lg:py-20">
          <div>
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-accent px-3.5 py-1 text-sm text-accent-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand))]" />
              শিক্ষকদের তৈরি, শিক্ষকদের জন্য
            </p>
            <h1 className="font-display text-[2rem] font-bold leading-[1.3] text-foreground sm:text-5xl sm:leading-[1.25]">
              একজনের বানানো প্রশ্নপত্র,
              <span className="block text-primary">আরেকজনের কাজে লাগুক</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
              শিক্ষকদের নিজের হাতে বানানো প্রশ্নপত্র খুঁজে নিন, দেখে নিন, নিজের একাউন্টে কপি করে নিজের মতো করে নিন।
            </p>

            <form onSubmit={submit} role="search" className="relative mt-7 max-w-xl">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                type="search"
                aria-label="প্রশ্নপত্র খুঁজুন"
                placeholder="যেমন: অষ্টম শ্রেণি গণিত"
                className="h-14 w-full rounded-2xl border border-input bg-card pl-12 pr-28 text-base shadow-lift placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-search-cancel-button]:hidden"
              />
              <Button type="submit" className="absolute right-2 top-1/2 h-10 -translate-y-1/2 px-5">
                খুঁজুন
              </Button>
            </form>

            <p className="mt-5 text-sm text-muted-foreground">
              নিজে বানিয়ে দেখতে চান?{" "}
              <Link to="/try" className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline">
                <FlaskConical className="h-4 w-4" aria-hidden="true" />
                সাইন আপ ছাড়াই Live Try করুন
              </Link>
            </p>
          </div>
          <HeroArt />
        </div>
      </section>

      {/* শ্রেণি/বিষয় ধরে ব্রাউজ */}
      {(classes || subjects) && (
        <section className="container py-12 sm:py-14">
          {classes && (
            <div className="mb-10">
              <SectionHead title="শ্রেণি অনুযায়ী খুঁজুন" />
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 lg:grid-cols-5">
                {classes.terms.map((m) => (
                  <Link
                    key={m.value}
                    to={`/papers?class=${encodeURIComponent(m.value)}`}
                    className="group flex items-center justify-between gap-2 rounded-2xl border border-border bg-card px-4 py-3 text-[15px] shadow-card sm:py-3.5 sm:text-base transition hover:border-primary/40 hover:shadow-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-safe:hover:-translate-y-0.5"
                  >
                    <span className="font-medium text-foreground">{m.label_bn}</span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </div>
          )}
          {subjects && (
            <div>
              <SectionHead title="বিষয় অনুযায়ী" />
              <div className="flex flex-wrap gap-2.5">
                {subjects.terms.map((m) => (
                  <Link
                    key={m.value}
                    to={`/papers?subject=${encodeURIComponent(m.value)}`}
                    style={{ "--tone-h": toneHue(m.value) }}
                    className="tone inline-flex items-center gap-2 rounded-full bg-[var(--tone-bg)] px-4 py-2 text-[15px] font-medium text-[var(--tone-ink)] transition hover:bg-[var(--tone-bg-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="h-2 w-2 rounded-full bg-[var(--tone-ink-soft)]" />
                    {m.label_bn}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* সাম্প্রতিক / জনপ্রিয় */}
      <section className="container pb-12 sm:pb-14">
        <SectionHead
          title={tab === "popular" && hasPopular ? "সবচেয়ে বেশি কপি হওয়া" : "সম্প্রতি যোগ হয়েছে"}
          action={
            <Link to="/papers" className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline">
              সব দেখুন
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          }
        />

        {hasPopular && (
          <div className="mb-5 inline-flex rounded-xl bg-muted p-1" role="tablist" aria-label="সাজানোর ধরন">
            {[
              ["recent", "নতুন"],
              ["popular", "জনপ্রিয়"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={cn(
                  "rounded-lg px-4 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  tab === key ? "bg-card font-medium text-foreground shadow-card" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {data.failed ? (
          <EmptyState title="প্রশ্নপত্র লোড করা যাচ্ছে না">ইন্টারনেট সংযোগ দেখে একটু পরে আবার চেষ্টা করুন।</EmptyState>
        ) : shown === null ? (
          <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3" aria-busy="true">
            {[1, 2, 3].map((i) => (
              <PaperCardSkeleton key={i} />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <EmptyState
            title="এখনো কোনো প্রশ্নপত্র শেয়ার হয়নি"
            actions={
              <Button asChild>
                <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                  মূল অ্যাপে যান
                </a>
              </Button>
            }
          >
            প্রথম প্রশ্নপত্রটা আপনার হাত থেকেই আসতে পারে।
          </EmptyState>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
            {shown.map((p) => (
              <PaperCard key={p.id} paper={p} />
            ))}
          </div>
        )}
      </section>

      {/* কীভাবে কাজ করে */}
      <section className="border-y border-border bg-card">
        <div className="container py-12 sm:py-14">
          <SectionHead title="তিন ধাপে" />
          <ol className="grid gap-4 sm:grid-cols-3 sm:gap-6">
            {STEPS.map(({ icon: Icon, title, text }, i) => (
              <li key={title} className="flex gap-4 rounded-2xl bg-background p-5 sm:flex-col">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent text-primary">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground">
                    <span className="mr-1.5 text-muted-foreground">{["১", "২", "৩"][i]}।</span>
                    {title}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* শেয়ার করার আমন্ত্রণ */}
      <section className="container pt-12 sm:pt-14">
        <div className="relative isolate overflow-hidden rounded-3xl bg-primary px-6 py-10 text-primary-foreground sm:px-12 sm:py-14">
          <div
            className="absolute inset-0 -z-10 opacity-[0.06]"
            style={{ backgroundImage: "repeating-linear-gradient(to bottom, transparent 0, transparent 35px, #fff 35px, #fff 36px)" }}
            aria-hidden="true"
          />
          <div className="max-w-2xl">
            <h2 className="font-display text-2xl font-bold leading-snug sm:text-3xl">আপনার বানানো প্রশ্নপত্র অন্যের কাজে লাগুক</h2>
            <p className="mt-3 text-primary-foreground/85">
              মূল অ্যাপে যে কোনো প্রশ্নপত্রের মেনু থেকে "গ্যালারিতে শেয়ার করুন" চাপলেই হলো। যাচাইয়ের পর এখানে প্রকাশ হবে।
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90">
                <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                  মূল অ্যাপে যান
                  <ArrowRight aria-hidden="true" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/40 bg-transparent text-primary-foreground hover:bg-white/10 hover:text-primary-foreground">
                <Link to="/try">Live Try করুন</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
